import { useEffect, useState } from "react";
import { TUseKeyPressKeys, TUseKeyPressOptions } from ".";

const useKeyPress = (
    keysArg: TUseKeyPressKeys,
    {
        preventDefault = true,
        inOrder = true,
        callback,
        listenerElement = window,
        multipress = false,
    }: TUseKeyPressOptions = {}
) => {
    const keys = keysArg instanceof Array ? keysArg : [keysArg];

    const [isPressed, setIsPressed] = useState(false);
    const [pressedKeys, setPressedKeys] = useState<string[]>([]);

    useEffect(() => {
        if (isPressed) {
            callback?.(pressedKeys);
        }
    }, [isPressed]);

    useEffect(() => {
        if (!pressedKeys.length) {
            setIsPressed(false);
            return;
        }

        if (multipress) {
            if (inOrder) {
                if (keys.every((key, index) => key === pressedKeys?.[index])) {
                    setIsPressed(true);
                    return;
                }
            } else {
                if (keys.every((key) => pressedKeys.some((pressedKey) => pressedKey === key))) {
                    setIsPressed(true);
                    return;
                }
            }
        } else {
            if (keys.some((key) => pressedKeys.some((pressedKey) => pressedKey === key))) {
                setIsPressed(true);
                return;
            }
        }
        setIsPressed(false);
    }, [keys, pressedKeys]);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (keys.some((key) => key === e.key) && preventDefault) {
            e.preventDefault();
        }

        setPressedKeys((prev) => {
            const newVal = [...prev];
            const index = newVal.indexOf(e.key);
            if (index === -1) {
                newVal.push(e.key);
                return newVal;
            }

            return prev;
        });
    };

    const handleKeyUp = (e: KeyboardEvent) => {
        setPressedKeys((prev) => {
            const newVal = [...prev];
            const index = newVal.indexOf(e.key);
            if (index !== -1) {
                newVal.splice(index, 1);
                return newVal;
            }

            return prev;
        });
    };

    const handleBlur = () => {
        setPressedKeys([]);
    };

    useEffect(() => {
        if (!listenerElement) {
            return;
        }

        listenerElement.addEventListener("keydown", handleKeyDown);
        listenerElement.addEventListener("keyup", handleKeyUp);
        listenerElement.addEventListener("blur", handleBlur);
        return () => {
            listenerElement.removeEventListener("keydown", handleKeyDown);
            listenerElement.removeEventListener("keyup", handleKeyUp);
            listenerElement.removeEventListener("blur", handleBlur);
        };
    }, [listenerElement]);

    return isPressed;
};

export default useKeyPress;
